/**
 * Format and parse date and datetime to US default. Generic locales are not supported yet.
 * Internally it handles everything as a Javascript Date object, the only specialty is
 * handling server dates (without any time component - e.g. time part is 00:00:00.000). Those must be treated an transfered
 * as local date. Converting them to UTC (or an ISO format) will break things.
 *
 * See date-formatter-test to see how it behaves in action.
 *
 * Good to know:
 *  - JavaScript's Date object tracks time in UTC internally,
 *    but typically accepts input and produces output in the local
 *    time of the computer it's running on.
 *
 * Implementation details:
 *  - 2020-12-31 or 2020-12-31Z
 *     --> we parse 2020-12-31 in _locale_ timezone, UTC will be something wild based on the time-zone.
 *
 */
import {
    addDays,
    addMonths,
    addYears,
    differenceInDays,
    endOfYear,
    format,
    formatDistanceToNowStrict,
    getYear,
    isFuture,
    isSameDay,
    isValid,
    isYesterday,
    startOfDay,
    startOfYear,
} from 'date-fns';
import { format as formatTz, utcToZonedTime } from 'date-fns-tz';

export type DateFormat = 'HUMAN' | 'SERVER' | 'PRECISE';
export type TimeFormat = 'HUMAN' | 'PRECISE';

/**
 * Returns the IANA timezone string, e.g.: Europe/Budapest
 */
export const getTimeZone = (): string => {
    return new Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const isDateWithoutUTCTime = (date: Date) =>
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0;

const isDateWithoutLocalTime = (date: Date) =>
    date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0 && date.getMilliseconds() === 0;

const isDateWithoutTime = (date: Date) => {
    return isDateWithoutUTCTime(date) || isDateWithoutLocalTime(date);
};

const nukeTimeComponent = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
};

const addLeadingZeros = (number: number, targetLength: number) => {
    const sign = number < 0 ? '-' : '';
    let output = Math.abs(number).toString();
    while (output.length < targetLength) {
        output = '0' + output;
    }
    return sign + output;
};

export const formatLocaleDate = (date: Date, addTrailingZ: boolean = true) => {
    const year = addLeadingZeros(date.getFullYear(), 4);
    const month = addLeadingZeros(date.getMonth() + 1, 2);
    const day = addLeadingZeros(date.getDate(), 2);
    return `${year}-${month}-${day}${addTrailingZ ? 'Z' : ''}`;
};

const formatUTCDate = (date: Date) => {
    const year = addLeadingZeros(date.getUTCFullYear(), 4);
    const month = addLeadingZeros(date.getUTCMonth() + 1, 2);
    const day = addLeadingZeros(date.getUTCDate(), 2);
    return `${year}-${month}-${day}Z`;
};

const formatUTCDateTime = (date: Date) => {
    const year = addLeadingZeros(date.getUTCFullYear(), 4);
    const month = addLeadingZeros(date.getUTCMonth() + 1, 2);
    const day = addLeadingZeros(date.getUTCDate(), 2);
    const hours = addLeadingZeros(date.getUTCHours(), 2);
    const minutes = addLeadingZeros(date.getUTCMinutes(), 2);
    const seconds = addLeadingZeros(date.getUTCSeconds(), 2);
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
};

const formatAsZoned = (date: Date, pattern: string) => {
    // 💖 time zones 💖
    return formatTz(utcToZonedTime(date, getTimeZone()), pattern, { timeZone: getTimeZone() });
};

/**
 * Parsing dates from the web app or the backend services.
 *
 * If the time is in UTC, it must have a 'Z' at the end. Z is the zone designator for the zero UTC offset.
 * Otherwise it will be treated as a reguler time-zoned date.
 *
 */
export const parseLocalDate = (dateStr: string): Date => {
    if (dateStr.length < 12) {
        const date = utcToZonedTime(dateStr.endsWith('Z') ? dateStr : `${dateStr}Z`, 'UTC');
        // Manual validation is required as utcToZonedTime returns a Date with NaN value instead of raising a RangeError
        if (!isValid(date)) {
            throw new Error('Invalid date encountered by parseLocalDate in date-formatter');
        }

        return date;
    }
    return new Date(dateStr);
};

export const parseServerDateTime = (dateTime: string): Date => {
    return parseLocalDate(dateTime.length >= 12 ? normalizeLocalDateTimeToServer(dateTime) : dateTime);
};

export const formatDate = (date: Date | string | null | undefined, dateFormat: string = 'HUMAN'): string => {
    if (!date) {
        return '';
    }
    if (typeof date === 'string') {
        return formatDate(parseLocalDate(date), dateFormat);
    }

    if (dateFormat === 'HUMAN') {
        if (isDateWithoutTime(date)) {
            return format(date, 'MMM d, yyyy');
        }
        return formatAsZoned(date, 'MMM d, yyyy');
    } else if (dateFormat === 'SERVER') {
        // here we try to guess which part of the datetime to use for date processing
        // the local?
        if (isDateWithoutLocalTime(date)) {
            return formatLocaleDate(date);
        }
        // the UTC?
        if (isDateWithoutUTCTime(date)) {
            return formatUTCDate(date);
        }

        return formatAsZoned(date, "yyyy-MM-dd'Z'");
    }

    // default to something not very awful
    return format(date, dateFormat);
};

export const formatDateTime = (date: Date | string | null | undefined, dateFormat: DateFormat = 'HUMAN'): string => {
    if (!date) {
        return '';
    }
    if (typeof date === 'string') {
        return formatDateTime(parseLocalDate(date), dateFormat);
    }

    if (dateFormat === 'HUMAN') {
        if (isDateWithoutTime(date)) {
            return format(nukeTimeComponent(date), "MMM d, yyyy 12:00 'AM'"); // midnight
        }
        return formatAsZoned(date, 'MMM d, yyyy hh:mm aa');
    } else if (dateFormat === 'SERVER') {
        return formatUTCDateTime(date);
    } else if (dateFormat === 'PRECISE') {
        return formatAsZoned(date, 'MMM d, yyyy HH:mm:ss');
    }

    // default to something not very awful
    return format(date, dateFormat);
};

export const formatRelativeDateTime = (date: Date | string): string => {
    const parsedDate = typeof date === 'string' ? parseServerDateTime(date) : date;

    if (Math.abs(Date.now() - parsedDate.getTime()) < 60_000) {
        return 'just now';
    }

    if (isYesterday(parsedDate)) {
        return 'Yesterday';
    }

    return formatDistanceToNowStrict(parsedDate, { addSuffix: true });
};

export const formatTime = (date: Date | string | null | undefined, timeFormat: TimeFormat = 'HUMAN'): string => {
    if (!date) {
        return '';
    }
    if (typeof date === 'string') {
        return formatTime(parseLocalDate(date), timeFormat);
    }

    if (timeFormat === 'HUMAN') {
        return formatAsZoned(date, 'hh:mm aa');
    } else if (timeFormat === 'PRECISE') {
        return formatAsZoned(date, 'HH:mm:ss');
    }
    return formatAsZoned(date, timeFormat);
};

export const todayDate = (): Date => {
    return startOfDay(new Date());
};

export const todayDateFormatted = (): string => {
    return formatDate(todayDate(), 'SERVER');
};

export const tomorrowDate = (): Date => {
    return addDays(startOfDay(new Date()), 1);
};

export const tomorrowDateFormatted = (): string => {
    return formatDate(tomorrowDate(), 'SERVER');
};

export const beginningOfThisYear = (): Date => {
    return startOfYear(new Date());
};

export const beginningOfNextYear = (): Date => {
    return addYears(startOfYear(new Date()), 1);
};

export const endOfThisYear = (): Date => {
    return endOfYear(new Date());
};

export const endOfNextYear = (): Date => {
    return addYears(endOfYear(new Date()), 1);
};

export const beginningOfThisYearFormatted = (): string => {
    return formatDate(beginningOfThisYear(), 'SERVER');
};

export const getThisYear = (): number => {
    return getYear(todayDate());
};

export const getNextYear = (): number => {
    return getYear(todayDate()) + 1;
};

export const formatDuration = (durationInMinutes: number) => {
    if (durationInMinutes === 0) {
        return '0 minutes';
    }

    const hours = Math.floor(durationInMinutes / 60);
    const minutes = durationInMinutes % 60;

    if (hours > 0) {
        return `${hours} hour${hours > 1 ? 's' : ''}${minutes > 0 ? ` ${minutes} minute${minutes > 1 ? 's' : ''}` : ''}`;
    }

    return `${minutes} minute${minutes > 1 ? 's' : ''}`;
};

export const formatDurationMillis = (milliseconds: number | null | undefined): string => {
    if (milliseconds === null || milliseconds === undefined) {
        return '-';
    }

    if (milliseconds < 1000) {
        return `0.${milliseconds} sec`;
    }
    if (milliseconds < 600000) {
        return `${Math.floor(milliseconds / 1000)}.${Math.floor((milliseconds % 1000) / 100)} seconds`;
    }
    if (milliseconds < 3600000) {
        const minutes = Math.floor(milliseconds / 60000);
        return `${minutes} minutes ${Math.floor((milliseconds - minutes * 60000) / 1000)} seconds`;
    }

    return `${milliseconds / 1000} seconds`;
};

export const differenceInDaysFromToday = (pastDate: string): number => {
    return differenceInDays(todayDate(), parseLocalDate(pastDate));
};

export const addMonthToToday = (month: number | null | undefined) => {
    if (!month) {
        return todayDate();
    }
    return addMonths(todayDate(), month);
};

export const findNextAvailableDay = (disabledDates: Date[], date = addDays(todayDate(), 1)): Date => {
    const isTaken = disabledDates.some((disabledDate) => isSameDay(new Date(disabledDate), date));
    if (isTaken) {
        return findNextAvailableDay(disabledDates, addDays(date, 1));
    }

    return date;
};

export const isFutureDate = (date: Date | string | number): boolean => {
    return typeof date === 'string' ? isFutureDate(parseLocalDate(date)) : isFuture(date);
};

/**
 * Converts a server date string (e.g., "2024-01-15T00:00:00Z") to a form date string (e.g., "2024-01-15")
 */
export const convertServerDateToFormDate = (serverDate: string): string => {
    return new Date(serverDate).toISOString().split('T')[0];
};

/**
 * Normalizes a LocalDateTime string (e.g., "2026-04-08T11:28:36") to the server-expected
 * ISO format with milliseconds and Z suffix (e.g., "2026-04-08T11:28:36.000Z").
 * This is a pure string operation — no Date parsing or timezone conversion is applied.
 */
export const normalizeLocalDateTimeToServer = (dateTime: string): string => {
    if (dateTime.endsWith('Z')) return dateTime;
    if (!dateTime.includes('.')) return dateTime + '.000Z';
    return dateTime + 'Z';
};

/**
 * Converts a form date string (e.g., "2024-01-15") to a server date string (e.g., "2024-01-15Z")
 */
export const convertFormDateToServerDate = (formDate: string): string => {
    if (!formDate.endsWith('Z')) {
        return formDate + 'Z';
    } else {
        return formDate;
    }
};
